import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  AlertTriangle,
  Award,
  Banknote,
  BellRing,
  BriefcaseBusiness,
  Check,
  Clock,
  Eye,
  EyeOff,
  GraduationCap,
  HeartHandshake,
  Palmtree,
  Sparkles,
  Timer,
  UserMinus,
  type LucideIcon,
} from 'lucide-react';
import type {
  NeedsAttentionAction,
  NeedsAttentionHideOptions,
  NeedsAttentionHideReason,
  NeedsAttentionHideUntil,
  NeedsAttentionItem,
  NeedsAttentionSeverity,
} from '@gokrew/models/company-dashboard';
import type { TeamOverlap } from '@gokrew/models/leave-management';
import { Tone } from '@gokrew/models/ui.types';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { TeamOverlapLine } from '@/components/LeaveDecision';
import { TooltipWrapper } from '@/components/TooltipWrapper';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  isSignalItem,
  needsAttentionActions,
  needsAttentionDisplayParams,
} from '@/utility/needsAttention';
import { formatNotificationTime } from '@/utility/notifications';
import { reloadNamespaceOnce } from '@/utility/i18nReload';
import { WhyLink } from './WhyLink';
import styles from './NeedsAttention.module.css';

const isTeamOverlap = (value: unknown): value is TeamOverlap =>
  typeof value === 'object' && value !== null && 'count' in value;

const SEVERITY_ICON: Record<NeedsAttentionSeverity, LucideIcon> = {
  danger: AlertTriangle,
  warning: AlertCircle,
  info: Clock,
};

/** A signal line's icon names its family (spec 8.1), not its urgency. */
const SIGNAL_ICON: Record<string, LucideIcon> = {
  pay_review_due: Banknote,
  vacation_expiring: Palmtree,
  vacation_none_taken: Palmtree,
  work_anniversary: Award,
  training_overdue: GraduationCap,
  retention_check_in: HeartHandshake,
  overtime_high: Timer,
  team_departures: UserMinus,
  job_open_long: BriefcaseBusiness,
};

/** The hide choices of a signal line (D143): 30 days, 90 days, until it changes. */
export type NeedsAttentionHideChoice = NeedsAttentionHideUntil | 'change';

const HIDE_CHOICES: NeedsAttentionHideChoice[] = ['30d', '90d', 'change'];
const HIDE_REASONS: NeedsAttentionHideReason[] = ['not_relevant', 'handled'];
const HIDE_CHOICE_KEY: Record<NeedsAttentionHideChoice, string> = {
  '30d': 'needs_attention.actions.hide_30',
  '90d': 'needs_attention.actions.hide_90',
  change: 'needs_attention.actions.hide_until_change',
};
const HIDE_REASON_KEY: Record<NeedsAttentionHideReason, string> = {
  not_relevant: 'needs_attention.actions.reason_not_relevant',
  handled: 'needs_attention.actions.reason_handled',
};

/**
 * The hide control of a signal line: hide for 30 or 90 days, or until the
 * line changes, with an optional reason (one tap, the menu stays open).
 */
const SignalHideMenu = ({
  item,
  namespace,
  choices,
  disabled,
  onHide,
}: {
  item: NeedsAttentionItem;
  namespace: string;
  choices: NeedsAttentionHideChoice[];
  disabled: boolean;
  onHide: (
    item: NeedsAttentionItem,
    options?: NeedsAttentionHideOptions
  ) => void;
}) => {
  const { t } = useTranslation(namespace);
  const [reason, setReason] = useState<NeedsAttentionHideReason | null>(null);
  const hideLabel = t('needs_attention.hide');
  return (
    <DropdownMenu>
      <TooltipWrapper title={hideLabel} disabled={disabled}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="xs"
            className={styles.hide}
            IconPrefix={EyeOff}
            aria-label={hideLabel}
            disabled={disabled}
          />
        </DropdownMenuTrigger>
      </TooltipWrapper>
      <DropdownMenuContent align="end" className={styles.hideMenu}>
        {choices.map((choice) => (
          <DropdownMenuItem
            key={choice}
            onSelect={() =>
              onHide(item, {
                ...(choice === 'change' ? {} : { until: choice }),
                ...(reason ? { reason } : {}),
              })
            }
          >
            {t(HIDE_CHOICE_KEY[choice])}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className={styles.hideMenuLabel}>
          {t('needs_attention.actions.reason_label')}
        </DropdownMenuLabel>
        {HIDE_REASONS.map((value) => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={reason === value}
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(checked) => setReason(checked ? value : null)}
          >
            {t(HIDE_REASON_KEY[value])}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface NeedsAttentionRowProps {
  item: NeedsAttentionItem;
  /** The portal namespace holding `needs_attention.*`. */
  namespace: string;
  onAction: (item: NeedsAttentionItem, action: NeedsAttentionAction) => void;
  /** Set while this row's "Notify" request is in flight. */
  pinging?: boolean;
  /**
   * Shows the quiet "Hide" eye at the far right, after the buttons. Copy:
   * `needs_attention.hide` in the portal namespace. On a signal line it opens
   * the hide menu (`hideChoices`) and passes the chosen options.
   */
  onHide?: (
    item: NeedsAttentionItem,
    options?: NeedsAttentionHideOptions
  ) => void;
  /** The hide choices of a signal line (default: 30 days, 90 days, change). */
  hideChoices?: NeedsAttentionHideChoice[];
  /** Set while this row's hide request is in flight. */
  hiding?: boolean;
  /**
   * A row of the hidden list: who hid it and when under the sentence, and a
   * single "Show again" button in place of the actions
   * (`needs_attention.unhide`, `needs_attention.hidden_by[_unknown]`).
   */
  onUnhide?: (item: NeedsAttentionItem) => void;
  /**
   * "Prepare the meeting" in the "Why?" popover of a line with `prepare`
   * (check-in, vacation, overtime). Copy: `needs_attention.actions.prepare_meeting`.
   */
  onPrepare?: (item: NeedsAttentionItem) => void;
}

/**
 * One line of a "Needs attention" feed, as its own separated row: a severity
 * marker, the sentence (plus, on leave lines, who else is off, and any
 * context `hints`, e.g. a manager's probation recommendation) and up to two
 * small buttons, primary first (three on a My team probation). A "Notify" button turns into a disabled
 * "Pinged 2 hours ago" (or its own `<label>_sent` copy) once sent. With
 * `onHide`, a quiet eye at the far right hides the line; with `onUnhide` the
 * row is one of the hidden list. Shared by the company dashboard and the
 * manager's My team.
 */
export const NeedsAttentionRow = memo(
  ({
    item,
    namespace,
    onAction,
    pinging = false,
    onHide,
    hiding = false,
    onUnhide,
    onPrepare,
    hideChoices = HIDE_CHOICES,
  }: NeedsAttentionRowProps) => {
    // Re-render once a reloaded namespace lands, not only on a language change.
    const { t, i18n } = useTranslation(namespace, {
      bindI18n: 'languageChanged loaded',
    });
    const signal = isSignalItem(item);
    const Icon = signal
      ? (SIGNAL_ICON[item.rollupOf ?? item.type] ?? Sparkles)
      : SEVERITY_ICON[item.severity];
    const params = needsAttentionDisplayParams(item.params, t, i18n.language);
    // A tab opened before a release can meet a line type its copy does not
    // know yet. Fetch the namespace again (once) and hide the line until its
    // words arrive, rather than printing a raw translation key.
    const known = i18n.exists(item.titleKey, { ns: namespace, ...params });
    useEffect(() => {
      if (!known) reloadNamespaceOnce(i18n, namespace);
    }, [known, i18n, namespace]);
    if (!known) return null;
    // Leave lines carry who on the team is off on those dates: that is
    // decision context, so it stays. The grey module label does not (JP,
    // 2026-10-02): the sentence already says what the line is about.
    const overlap = isTeamOverlap(item.params.teamOverlap)
      ? item.params.teamOverlap
      : null;
    const actions = onUnhide ? [] : needsAttentionActions(item);
    const hiddenAt = item.hidden?.at;
    const hideLabel = t('needs_attention.hide');
    return (
      // Two buttons stay on the right, and wrap under the sentence only when
      // the row is too narrow for both (UX review 2026-10-02, P2-3).
      <li
        className={cn(
          styles.row,
          styles[item.severity],
          actions.length > 1 && styles.stacked,
          onUnhide && styles.hiddenRow,
          signal && styles.signalRow
        )}
      >
        <span className={styles.marker} aria-hidden="true" />
        <Icon className={styles.icon} aria-hidden="true" />
        <span className={styles.body}>
          <span className={styles.text}>
            {t(item.titleKey, params)}
            {item.isNew && !onUnhide && (
              <Chip type={Tone.PRIMARY} size="sm" className={styles.newChip}>
                {t('needs_attention.signals.new')}
              </Chip>
            )}
          </span>
          {overlap && !onUnhide && (
            <span className={styles.meta}>
              <TeamOverlapLine overlap={overlap} />
            </span>
          )}
          {!onUnhide &&
            item.hints?.map((hint) => (
              <span
                key={hint.key}
                className={cn(
                  styles.meta,
                  styles.hint,
                  hint.tone === 'warning' && styles.hintWarning
                )}
              >
                {t(
                  hint.key,
                  needsAttentionDisplayParams(
                    hint.params ?? {},
                    t,
                    i18n.language
                  )
                )}
              </span>
            ))}
          {!onUnhide && !item.rollupOf && (
            <WhyLink item={item} namespace={namespace} onPrepare={onPrepare} />
          )}
          {onUnhide && hiddenAt && (
            <span className={styles.meta}>
              {item.hidden?.byName
                ? t('needs_attention.hidden_by', {
                    name: item.hidden.byName,
                    time: formatNotificationTime(hiddenAt, i18n.language),
                  })
                : t('needs_attention.hidden_by_unknown', {
                    time: formatNotificationTime(hiddenAt, i18n.language),
                  })}
            </span>
          )}
        </span>
        <span className={styles.actions}>
          {onUnhide && (
            <Button
              variant="outline"
              size="xs"
              className={styles.action}
              IconPrefix={Eye}
              disabled={hiding}
              onClick={() => onUnhide(item)}
            >
              {t('needs_attention.unhide')}
            </Button>
          )}
          {actions.map((action, index) => {
            const pingedAt = action.ping?.lastPingedAt;
            return (
              <Button
                key={action.labelKey}
                variant={index === 0 ? 'primary' : 'outline'}
                size="xs"
                className={styles.action}
                disabled={!!pingedAt || (!!action.ping && pinging)}
                IconPrefix={action.ping ? (pingedAt ? Check : BellRing) : null}
                onClick={() => onAction(item, action)}
              >
                {pingedAt
                  ? // A button may name its own sent state ("Reminder sent
                    // 2h ago"); the rest read "Pinged 2h ago".
                    t([`${action.labelKey}_sent`, 'needs_attention.pinged'], {
                      time: formatNotificationTime(pingedAt, i18n.language),
                    })
                  : // A rollup's button names its count ("See these 4").
                    t(action.labelKey, {
                      ...params,
                      defaultValue: t('needs_attention.actions.open'),
                    })}
              </Button>
            );
          })}
          {onHide && !onUnhide && signal && (
            <SignalHideMenu
              item={item}
              namespace={namespace}
              choices={hideChoices}
              disabled={hiding}
              onHide={onHide}
            />
          )}
          {onHide && !onUnhide && !signal && (
            // Quiet on purpose: it must never compete with the action.
            <TooltipWrapper title={hideLabel} disabled={hiding}>
              <Button
                variant="ghost"
                size="xs"
                className={styles.hide}
                IconPrefix={EyeOff}
                aria-label={hideLabel}
                disabled={hiding}
                onClick={() => onHide(item)}
              />
            </TooltipWrapper>
          )}
        </span>
      </li>
    );
  }
);
NeedsAttentionRow.displayName = 'NeedsAttentionRow';

/**
 * The "Signals" sub-header between the actions and the signal lines of a
 * feed (D134), as a list item. Copy: `needs_attention.signals.group_title`.
 */
export const NeedsAttentionSignalsHeader = ({
  namespace,
}: {
  namespace: string;
}) => {
  const { t } = useTranslation(namespace);
  return (
    <li className={styles.groupHeader} role="presentation">
      <Sparkles className={styles.groupHeaderIcon} aria-hidden="true" />
      {t('needs_attention.signals.group_title')}
    </li>
  );
};

export interface NeedsAttentionHiddenLinkProps {
  /** Hidden lines. Nothing renders at 0. */
  count: number;
  /** The portal namespace holding `needs_attention.hidden_link`. */
  namespace: string;
  /** The list is open: the link offers to close it. */
  expanded?: boolean;
  onClick: () => void;
  className?: string;
}

/**
 * The quiet "3 hidden · Show" link at the bottom of a feed card: opens the
 * hidden lines, each with "Show again", so a misclick is never permanent.
 */
export const NeedsAttentionHiddenLink = ({
  count,
  namespace,
  expanded = false,
  onClick,
  className,
}: NeedsAttentionHiddenLinkProps) => {
  const { t } = useTranslation(namespace);
  if (count <= 0) return null;
  return (
    <button
      type="button"
      className={cn(styles.hiddenLink, className)}
      aria-expanded={expanded}
      onClick={onClick}
    >
      <EyeOff className={styles.hiddenLinkIcon} aria-hidden="true" />
      {t(
        expanded
          ? 'needs_attention.hidden_link_close'
          : 'needs_attention.hidden_link',
        { count }
      )}
    </button>
  );
};
